import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Search, ChevronRight, Plus, UserPlus, Filter, X, Pencil, FileText, CalendarDays,
} from 'lucide-react';

/**
 * Admin → Guest Sellers.
 * A "guest seller" rule = a GST-number-keyed plan + discount grant with a
 * validity window. Reuses the existing seller-promotions backend
 * (GET/POST /admin/promotions, GET /plans) — no duplicate models/plans.
 * The GST number is the eligibility identifier; the discount applies to the
 * registered seller whose GST exactly matches.
 */

const STATUS_MAP = {
  SCHEDULED: { label: 'Upcoming', cls: 'bg-sky-100 text-sky-700' },
  ACTIVE: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' },
  EXPIRED: { label: 'Expired', cls: 'bg-neutral-200 text-neutral-600' },
  DISABLED: { label: 'Disabled', cls: 'bg-rose-100 text-rose-600' },
};

function fmtDay(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Modal({ open, onClose, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-fab-primary-lighter">
        {children}
      </div>
    </div>
  );
}

const EMPTY_FORM = { gst_number: '', plan: '', discount_percentage: '', start_date: '', expiry_date: '' };

export default function AdminGuestSellers() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [plans, setPlans] = useState([]);

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // promo being edited (or null for add)
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/promotions').then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/plans').then(({ data }) => {
      const list = Array.isArray(data) ? data : (data.plans || data.items || []);
      setPlans(list.map((p) => ({ tier: p.tier, name: p.name || p.tier })));
    }).catch(() => setPlans([{ tier: 'STANDARD', name: 'Standard' }, { tier: 'PRO', name: 'Pro' }, { tier: 'ENTERPRISE', name: 'Enterprise' }]));
  }, []);

  const filtered = useMemo(() => {
    let out = items || [];
    const ql = q.trim().toLowerCase();
    if (ql) out = out.filter((it) => (it.gst_number || '').toLowerCase().includes(ql) || (it.business_name || '').toLowerCase().includes(ql));
    if (statusFilter) out = out.filter((it) => it.status === statusFilter);
    return out;
  }, [items, q, statusFilter]);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setModalOpen(true);
  }
  function openEdit(p) {
    setEditing(p);
    setForm({
      gst_number: p.gst_number || '',
      plan: p.plan || '',
      discount_percentage: String(p.discount_percentage ?? ''),
      start_date: (p.start_date || '').slice(0, 10),
      expiry_date: (p.expiry_date || '').slice(0, 10),
    });
    setErrors({});
    setModalOpen(true);
  }

  function validate() {
    const e = {};
    const gst = form.gst_number.replace(/\s+/g, '').toUpperCase();
    if (!gst) e.gst_number = 'GST number is required';
    else if (gst.length < 6) e.gst_number = 'Enter a valid GST number';
    if (!form.plan) e.plan = 'Plan is required';
    const disc = Number(form.discount_percentage);
    if (form.discount_percentage === '' || isNaN(disc)) e.discount_percentage = 'Discount is required';
    else if (disc <= 0) e.discount_percentage = 'Discount must be greater than 0';
    else if (disc > 100) e.discount_percentage = 'Discount cannot exceed 100%';
    if (!form.start_date) e.start_date = 'Start date is required';
    if (!form.expiry_date) e.expiry_date = 'End date is required';
    if (form.start_date && form.expiry_date && form.expiry_date <= form.start_date) {
      e.expiry_date = 'End date must be after start date';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.post('/admin/promotions', {
        gst_number: form.gst_number.replace(/\s+/g, '').toUpperCase(),
        plan: form.plan,
        discount_percentage: Number(form.discount_percentage),
        start_date: form.start_date,
        expiry_date: form.expiry_date,
      });
      toast.success(editing ? 'Guest seller rule updated' : 'Guest seller rule created');
      setModalOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save. Please try again.';
      setErrors((p) => ({ ...p, _root: msg }));
    } finally {
      setSaving(false);
    }
  }

  const inputCls = (field) => `w-full px-3 py-2.5 text-sm rounded-lg border ${errors[field] ? 'border-rose-400' : 'border-fab-primary-lighter'} bg-white focus:outline-none focus:ring-2 focus:ring-fab-primary/30`;

  return (
    <div data-testid="admin-guest-sellers">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-fab-text" data-testid="guest-title">Guest Sellers</h2>
          <p className="text-sm text-fab-text-secondary mt-0.5" data-testid="guest-subtitle">Manage guest sellers by applying plan and discount based on GST number.</p>
        </div>
        <button onClick={openAdd} data-testid="guest-add-link" className="flex items-center gap-3 group text-left">
          <span className="w-10 h-10 rounded-xl bg-fab-primary-lighter grid place-items-center text-fab-primary group-hover:bg-fab-primary group-hover:text-white transition">
            <UserPlus className="w-5 h-5" />
          </span>
          <span className="leading-tight">
            <span className="block text-[13px] font-semibold text-fab-primary">Add Guest Seller</span>
            <span className="block text-[11px] text-fab-text-muted">Configure GST number, plan and discount</span>
          </span>
        </button>
      </div>

      {/* Search / filter bar */}
      <div className="mt-4 bg-white border border-fab-primary-lighter rounded-xl p-3 flex items-center gap-2.5 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-fab-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="guest-search"
            placeholder="Search by GST number, seller name, email or contact..."
            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-fab-primary-lighter bg-fab-base/40 focus:outline-none focus:ring-2 focus:ring-fab-primary/30" />
        </div>
        <div className="relative">
          <button onClick={() => setShowFilters((s) => !s)} data-testid="guest-filters-btn"
            className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary hover:text-fab-primary transition">
            <Filter className="w-4 h-4" /> Filters{statusFilter ? ' (1)' : ''}
          </button>
          {showFilters && (
            <div className="absolute right-0 mt-2 w-60 bg-white border border-fab-primary-lighter rounded-xl shadow-xl p-3 z-20" data-testid="guest-filter-panel">
              <label className="block text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="guest-filter-status"
                className="w-full text-sm rounded-lg border border-fab-primary-lighter px-2 py-2 bg-white">
                <option value="">All statuses</option>
                <option value="SCHEDULED">Upcoming</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="DISABLED">Disabled</option>
              </select>
              <div className="flex justify-between mt-3">
                <button onClick={() => { setStatusFilter(''); }} data-testid="guest-filter-clear" className="text-[12.5px] text-fab-text-muted hover:text-fab-text">Clear</button>
                <button onClick={() => setShowFilters(false)} data-testid="guest-filter-apply" className="text-[12.5px] px-3 py-1.5 rounded-lg bg-fab-primary text-white">Apply</button>
              </div>
            </div>
          )}
        </div>
        <button onClick={openAdd} data-testid="guest-add-btn"
          className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-lg bg-fab-primary text-white hover:bg-fab-primary-dark transition shadow-sm">
          <Plus className="w-4 h-4" /> Add Guest
        </button>
      </div>

      {/* Table */}
      <div className="mt-4 bg-white border border-fab-primary-lighter rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-fab-text-muted border-b border-fab-primary-lighter">
                {['GST Number', 'Seller / Business Name', 'Plan', 'Discount %', 'Start Date', 'End Date', 'Status', 'Edit'].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items === null ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-fab-text-muted"><Loader2 className="inline w-5 h-5 animate-spin mr-2" /> Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16">
                  <div className="flex flex-col items-center text-center" data-testid="guest-empty">
                    <div className="w-16 h-16 rounded-2xl bg-fab-primary-lighter grid place-items-center text-fab-primary mb-3">
                      <FileText className="w-7 h-7" />
                    </div>
                    <div className="font-semibold text-fab-text text-[15px]">No Guest Sellers</div>
                    <div className="text-[13px] text-fab-text-muted mt-1 max-w-xs">Add a GST number to apply plan discounts to eligible sellers.</div>
                  </div>
                </td></tr>
              ) : filtered.map((p) => {
                const st = STATUS_MAP[p.status] || { label: p.status, cls: 'bg-neutral-100 text-neutral-500' };
                return (
                  <tr key={p.id} className="border-b border-fab-primary-lighter/60 hover:bg-fab-base/40" data-testid={`guest-row-${p.id}`}>
                    <td className="px-4 py-3 font-mono text-[12.5px] text-fab-text whitespace-nowrap">{p.gst_number || '—'}</td>
                    <td className="px-4 py-3 font-semibold text-fab-text">{p.business_name || '—'}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-fab-primary-lighter text-fab-primary text-[11px] font-semibold">{p.plan}</span></td>
                    <td className="px-4 py-3 font-semibold">{p.discount_percentage}%</td>
                    <td className="px-4 py-3 whitespace-nowrap text-fab-text-secondary">{fmtDay(p.start_date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-fab-text-secondary">{fmtDay(p.expiry_date)}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${st.cls}`}>{st.label}</span></td>
                    <td className="px-4 py-3">
                      <button onClick={() => openEdit(p)} data-testid={`guest-edit-${p.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary hover:text-fab-primary transition">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {items !== null && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-fab-primary-lighter text-[12.5px] text-fab-text-secondary" data-testid="guest-count">
            {filtered.length} guest seller rule{filtered.length === 1 ? '' : 's'}
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} testid="guest-modal">
        <div className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold text-fab-text">{editing ? 'Edit Guest Seller' : 'Add Guest Seller'}</h3>
              <p className="text-[12.5px] text-fab-text-muted mt-0.5">Configure GST number, plan and discount for the guest seller.</p>
            </div>
            <button onClick={() => setModalOpen(false)} className="text-fab-text-muted hover:text-fab-text" data-testid="guest-modal-close"><X className="w-5 h-5" /></button>
          </div>

          {errors._root && <div className="mt-3 bg-rose-50 border border-rose-200 text-rose-600 text-[12.5px] rounded-lg px-3 py-2" data-testid="gm-error">{errors._root}</div>}

          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-[12.5px] font-semibold text-fab-text mb-1">GST Number <span className="text-rose-500">*</span></label>
              <input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
                placeholder="Enter GST number (e.g. 27ABCDE1234F1Z5)" data-testid="gm-gst" className={inputCls('gst_number')} />
              {errors.gst_number ? <p className="text-[11px] text-rose-500 mt-1">{errors.gst_number}</p>
                : <p className="text-[11px] text-fab-text-muted mt-1">This GST number will be used to identify eligible sellers.</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[12.5px] font-semibold text-fab-text mb-1">Plan <span className="text-rose-500">*</span></label>
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="gm-plan" className={inputCls('plan')}>
                  <option value="">Select Plan</option>
                  {plans.map((p) => <option key={p.tier} value={p.tier}>{p.name}</option>)}
                </select>
                {errors.plan && <p className="text-[11px] text-rose-500 mt-1">{errors.plan}</p>}
              </div>
              <div>
                <label className="block text-[12.5px] font-semibold text-fab-text mb-1">Discount % <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <input type="number" min="1" max="100" value={form.discount_percentage}
                    onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
                    placeholder="Enter discount percentage" data-testid="gm-discount" className={`${inputCls('discount_percentage')} pr-8`} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-fab-text-muted text-sm">%</span>
                </div>
                {errors.discount_percentage && <p className="text-[11px] text-rose-500 mt-1">{errors.discount_percentage}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[12.5px] font-semibold text-fab-text mb-1">Start Date <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    data-testid="gm-start" className={inputCls('start_date')} />
                  <CalendarDays className="w-4 h-4 text-fab-text-muted absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                {errors.start_date && <p className="text-[11px] text-rose-500 mt-1">{errors.start_date}</p>}
              </div>
              <div>
                <label className="block text-[12.5px] font-semibold text-fab-text mb-1">End Date <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                    data-testid="gm-end" className={inputCls('expiry_date')} />
                  <CalendarDays className="w-4 h-4 text-fab-text-muted absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                {errors.expiry_date && <p className="text-[11px] text-rose-500 mt-1">{errors.expiry_date}</p>}
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button onClick={() => setModalOpen(false)} data-testid="gm-cancel"
              className="px-4 py-2.5 text-sm rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:bg-fab-base transition">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="gm-save"
              className="px-6 py-2.5 text-sm font-semibold rounded-lg bg-fab-primary text-white hover:bg-fab-primary-dark transition disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
